import { useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { FileDown, FileText } from "lucide-react";
import api, { errMsg } from "@/lib/api";

import { SendPdfButton } from "@/components/SendPdfButton";
import { ModuleDigestButton } from "@/components/ModuleDigestButton";
const clean = (s) => (s || "").replace(/\s+/g, " ").trim();

function collect() {
  const main = document.querySelector("main");
  if (!main) return null;
  const title = clean(main.querySelector("h1")?.innerText) || clean(document.title) || "Report";
  const filters = [...main.querySelectorAll("select, input[type=date], input[type=month]")].slice(0, 8)
    .map((el) => (el.tagName === "SELECT" ? el.options[el.selectedIndex]?.text : el.value)).filter((v) => v && !/^all /i.test(v));
  const kpis = [...main.querySelectorAll("[data-testid^='kpi-'], [data-testid*='-kpi-']")].filter((c) => !/-mtd$/.test(c.getAttribute("data-testid"))).map((c) => {
    const label = clean(c.querySelector(".pp-label")?.innerText);
    const value = clean(c.querySelector(".font-mono")?.innerText);
    const mtd = clean(c.querySelector("[data-testid$='-mtd'] .font-mono")?.innerText);
    return label && value ? [mtd ? `${label} (Day | MTD)` : label, mtd ? `${value} | MTD ${mtd}` : value] : null;
  }).filter(Boolean);
  const tables = [...main.querySelectorAll("table")].map((t, i) => {
    const headers = [...t.querySelectorAll("thead th")].map((th) => clean(th.innerText) || `Col ${i + 1}`);
    const rows = [...t.querySelectorAll("tbody tr")].map((tr) => [...tr.querySelectorAll("td")].map((td) => clean(td.innerText)))
      .filter((r) => r.length > 1 && r.some(Boolean));
    if (!headers.length || !rows.length) return null;
    const card = t.closest(".pp-card, .rounded-md, section, div");
    const name = clean(card?.querySelector(".pp-label, h2, h3")?.innerText) || `Table ${i + 1}`;
    const trimmed = headers.length && !headers[headers.length - 1] ? headers.slice(0, -1) : headers;
    return { name: name.slice(0, 40), headers: trimmed, rows: rows.map((r) => r.slice(0, trimmed.length)) };
  }).filter(Boolean);
  return { title, subtitle: filters.join(" · ") || undefined, kpis: kpis.length ? kpis : undefined, tables };
}

export function PageExport() {
  const { pathname } = useLocation();
  const [busy, setBusy] = useState(null);
  if (["/login", "/vendor", "/set-password", "/quote-accept"].includes(pathname)) return null;
  const run = async (fmt) => {
    const p = collect();
    if (!p || (!p.tables.length && !p.kpis)) return toast.info("Nothing to export on this page yet — load some data first.");
    setBusy(fmt);
    try {
      const res = await api.post("/export/page", { ...p, fmt }, { responseType: "blob" });
      const u = URL.createObjectURL(res.data); const a = document.createElement("a"); a.href = u; a.download = `${p.title.replace(/[^\w]+/g, "_")}.${fmt}`; a.click(); URL.revokeObjectURL(u);
      toast.success(`${fmt === "pdf" ? "PDF" : "Excel"} downloaded · ${p.tables.length} table(s)${p.kpis ? ` · ${p.kpis.length} KPIs` : ""}`);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(null); }
  };
  return (
    <div className="flex items-center justify-end gap-1.5 -mb-2" data-testid="page-export">
      <ModuleDigestButton />
      <span className="text-[10px] uppercase tracking-wider text-slate-400 mr-1 hidden sm:inline">Download this page</span>
      <button className="pp-btn-ghost h-8 px-2.5 text-xs" onClick={() => run("xlsx")} disabled={!!busy} title="Download everything on this page as Excel" data-testid="page-export-xlsx"><FileDown size={13} /> {busy === "xlsx" ? "…" : "Excel"}</button>
      <button className="pp-btn-ghost h-8 px-2.5 text-xs" onClick={() => run("pdf")} disabled={!!busy} title="Download everything on this page as a letterhead PDF" data-testid="page-export-pdf"><FileText size={13} /> {busy === "pdf" ? "…" : "PDF"}</button>
      <SendPdfButton module={pathname} filename={`${(collect()?.title || "Report").replace(/[^\w]+/g, "_")}.pdf`} defaultSubject={collect()?.title || "Report"} getBlob={async () => { const p = collect(); if (!p || (!p.tables.length && !p.kpis)) return null; return (await api.post("/export/page", { ...p, fmt: "pdf" }, { responseType: "blob" })).data; }} />
    </div>
  );
}
